import {
  QueryKey,
  UseQueryOptions,
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { isEqual } from 'lodash'
import { useCallback, useState } from 'react'
import { getQueryParamsFromUrl } from './utils/getQueryParamsFromUrl'

import { APIErrorHandler } from '../APIErrorHandler/APIErrorHandler'
import {
  PBPListResponse,
  PaginationProps,
  QueryResult,
} from './useQueryList.types'

export const useQueryList = <A extends PaginationProps, B>(
  queryKey: QueryKey,
  api: (_: A) => Promise<AxiosResponse<PBPListResponse<B>>>,
  data: A,
  options?: Omit<
    UseQueryOptions<AxiosResponse<PBPListResponse<B>>>,
    'queryFn' | 'queryKey'
  >
) => {
  const [prevData, setPrevData] = useState(data)
  const [queryData, setQueryData] = useState(data)
  // Pages visited via nextPage, so cursor-only endpoints (no `prev` link) can still go back
  const [pageHistory, setPageHistory] = useState<A[]>([])

  if (!isEqual(prevData, data)) {
    setPrevData(data)
    setQueryData(data)
    setPageHistory([])
  }

  const {
    data: resData,
    isLoading,
    isFetching,
    isError,
    status,
    error: _error,
    refetch: refresh,
  } = useQuery({
    ...options,
    queryKey: [...queryKey, queryData],
    queryFn: () => api(queryData),
    placeholderData: keepPreviousData,
  })

  const error = _error as AxiosError<{ error: Record<string, unknown> }>

  const refreshToFirstPage = useCallback(() => {
    setPageHistory([])
    setQueryData((qData) => {
      // `next` is the cursor param for next-token (cursor-only) endpoints
      const { cursor, page, next, ...refreshParams } = qData as {
        [key: string]: string | number
      }

      return refreshParams as A
    })
  }, [])

  const nextPage = useCallback(() => {
    const nextLink = resData?.data._links?.next

    if (!nextLink) return

    setPageHistory((history) => [...history, queryData])
    setQueryData({ ...queryData, ...getQueryParamsFromUrl(nextLink) } as A)
  }, [resData?.data._links?.next, queryData])

  const previousPage = useCallback(() => {
    const prevLink = resData?.data._links?.prev
    const previousQueryData = pageHistory[pageHistory.length - 1]

    if (!prevLink && !previousQueryData) return

    setPageHistory((history) => history.slice(0, -1))
    setQueryData(
      prevLink
        ? ({ ...queryData, ...getQueryParamsFromUrl(prevLink) } as A)
        : previousQueryData
    )
  }, [resData?.data._links?.prev, queryData, pageHistory])

  const getResult = (): QueryResult<B> => {
    const defaultResult: QueryResult<B> = {
      // NOTE: Data returned from Tanstack Query
      isLoading,
      isFetching,
      error: isError,
      data: resData?.data.data ?? null,
      status,

      // NOTE: Our custom data
      errorMessage: error
        ? APIErrorHandler(error?.response?.data?.error)
        : null,
      totalResults: 0,
    }

    if (status !== 'success') {
      return defaultResult
    }

    if (resData?.status === 204 || !resData?.data.data.length) {
      return { ...defaultResult, errorMessage: 'No results' }
    }

    // Cursor-paginated endpoints don't return a total; report it as unknown (null) rather than 0,
    // which tables treat as "no results"
    const totalResults = resData.data.totalResults ?? null

    return {
      ...defaultResult,
      errorMessage: '',
      totalResults,
    }
  }

  const responseActions = {
    nextPage: resData?.data._links?.next ? nextPage : null,
    previousPage:
      resData?.data._links?.prev || pageHistory.length > 0
        ? previousPage
        : null,
    refresh,
    refreshToFirstPage,
  }

  return [getResult(), responseActions] as const
}
